import React, {
  ChangeEvent,
  useCallback,
  useMemo,
  useState,
  useEffect
} from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { useRouter } from 'next/router';
import { Dispatch } from 'redux';
import dynamic from 'next/dynamic';
import { setError } from '../../actions';
import {
  fallbackStyles,
  ListWrapper,
  SearchContainer,
  SearchPlaceholder,
  Spinner,
  SpinnerWrapper
} from './styled';
import useScreenSize from '../../hooks/useScreenSize';
import { useClient } from '../../hooks/useClient';
import { PageHeader } from '../ui/PageHeader';
import { BackHistoryStorage } from '../../utils/helpers/BackHistoryStorage';
import { BusinessItemsListPageProps, BusinessItemsType } from './types';
import { HeaderTitle } from '../ui/PageHeader/styled';
import { getFilteredValue } from '../../utils/helpers/getFilteredValue';

const NewInput = dynamic(
  () => import('../ui/Input/NewInput').then(mod => mod.NewInput),
  {
    ssr: false,
    loading: SearchPlaceholder
  }
);

const FallbackView = dynamic(
  () => import('../FallbackView').then(mod => mod.FallbackView),
  { ssr: false }
);

const mapDispatchToProps = (dispatch: Dispatch) => ({
  setErrorUI: (errorState: boolean) => dispatch(setError(errorState))
});

const BusinessItemsListPageWrapperComponent: React.FC<
  BusinessItemsListPageProps
> = ({
  setErrorUI,
  title,
  hasError,
  items,
  CardComponent,
  ListContainer,
  type = BusinessItemsType.agencies,
  children,
  loading = false
}) => {
  const { t } = useTranslation();
  const router = useRouter();
  const { isSmall } = useScreenSize();
  const isClient = useClient();

  const hasHistory = useMemo(
    () => (isClient ? BackHistoryStorage.getBackHistoryStorage() : false),
    [isClient]
  );

  const showMobileHeader = (isSmall && isClient) || !isClient;
  const showDesktopTitle = (!isSmall && isClient) || !isClient;

  const [searchValue, setSearchValue] = useState('');

  const filteredItems = useMemo(
    () => getFilteredValue(items, searchValue),
    [items, searchValue]
  );

  const itemList = useMemo(
    () =>
      filteredItems?.length ? (
        filteredItems?.map(item => <CardComponent key={item.id} {...item} />)
      ) : (
        <FallbackView
          width={213}
          height={155}
          text={t('pages.business_pages.no_results')}
          icon="/static/images/new_ad/icons/icon-not-found.png"
          styles={fallbackStyles}
        />
      ),
    [filteredItems, CardComponent, t]
  );

  const onChangeHandler = (e: ChangeEvent<HTMLInputElement>) =>
    setSearchValue(e.target.value);

  const onResetHandler = useCallback(() => setSearchValue(''), []);

  const goBack = useCallback(() => {
    if (hasHistory) router.back();
    else router.push('/');
  }, [router, hasHistory]);

  useEffect(() => {
    if (hasError) setErrorUI(true);
  }, [hasError, setErrorUI]);

  const isEmptyView = !filteredItems?.length && !loading;
  const isResidencePage = type === BusinessItemsType.residences;
  const inputPlaceholder = t(
    isResidencePage ? 'residences.search' : 'pages.agencies.search'
  );

  return (
    <ListWrapper>
      {showMobileHeader && (
        <PageHeader
          id="business_items_page_header"
          title={title}
          backButtonCypress={`${type}-header-btn-close`}
          titleCypress={`${type}-page-title`}
          onClick={goBack}
          showHeaderBorder={false}
          headerTag="h1"
        />
      )}
      <SearchContainer $isResidencePage={isResidencePage}>
        {showDesktopTitle && (
          <HeaderTitle data-cy={`${type}-page-title`} tag="h1">
            {title}
          </HeaderTitle>
        )}
        <NewInput
          stat={`search-${isResidencePage ? 'complex' : 'agency'}`}
          name={`${type}-page-search`}
          placeholder={inputPlaceholder}
          autoComplete="on"
          onChangeHandler={onChangeHandler}
          value={searchValue}
          isShowSearch
          isShowReset={!!searchValue}
          onResetHandler={onResetHandler}
        />
        {children}
      </SearchContainer>
      {loading ? (
        <SpinnerWrapper>
          <Spinner />
        </SpinnerWrapper>
      ) : (
        <ListContainer $isEmptyView={isEmptyView} data-cy={`${type}-page-list`}>
          {itemList}
        </ListContainer>
      )}
    </ListWrapper>
  );
};

export const BusinessItemsListPageWrapper = connect(
  null,
  mapDispatchToProps
)(BusinessItemsListPageWrapperComponent);
